/**
 * Vasaloppet Mina Sidor
 * Author: Peter Löfås, peter@lofas.se
 */

import React, { Fragment } from 'react'
import FirebaseContext from '../providers/Firebase/context';
import { Typography, Card, CardHeader, CardContent, Theme, withStyles, createStyles, Button, Grid, CardActionArea, LinearProgress, CircularProgress } from '@material-ui/core';
import { Profile } from '../model/Profile';
import { ApiBackend } from '../providers/apibackend';
import { EntryStatistics } from '../model/EntryStatistics';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import Firebase from '../providers/Firebase/firebase';
import BarChart from './Common/BarChart';
import { OrderStatistics } from '../model/OrderStatistics';
import { SportsProSyncStatus } from '../model/SportsProSyncStatus';
import { IceOverview } from '../model/IceOverview';
import moment from 'moment';

interface State {
    entryStatistics: EntryStatistics;
    orderStatistics: OrderStatistics;

    entryStatisticsLoading: boolean;
    orderStatisticsLoading: boolean;

    sportsProSyncStatus: SportsProSyncStatus;
    sportsProSyncStatusLoading: boolean;

    iceOverview: IceOverview;
    iceOverviewLoading: boolean;
}

/**
 * Dashboard for logged in users
 */
class LoggedIn extends React.Component<RouteComponentProps, State> {
    static contextType = FirebaseContext;

    constructor(props: RouteComponentProps) {
        super(props);
        this.state = { entryStatistics: null, entryStatisticsLoading: true, orderStatistics: null, orderStatisticsLoading: true, sportsProSyncStatus: null, sportsProSyncStatusLoading: true, iceOverview: null, iceOverviewLoading: true };
    }

    componentDidMount() {
        let back = new ApiBackend();
        this.setState({ entryStatisticsLoading: true });
        back.getEntryStatistics().then((data) => {
            this.setState({ entryStatistics: data, entryStatisticsLoading: false });
        });
        back.getOrderStatistics().then((data) => {
            this.setState({ orderStatistics: data, orderStatisticsLoading: false });
        });
        back.getSportsProSyncStatus().then((data) => {
            this.setState({ sportsProSyncStatus: data, sportsProSyncStatusLoading: false });
        });
        back.getIceOverview().then((data) => {
            this.setState({ iceOverview: data, iceOverviewLoading: false });
        });
    }

    saveProfileChanges = () => {
    }

    onchange = (prop: string) => (ev: React.ChangeEvent<HTMLInputElement>) => {
        let o = {} as any;
        o[prop] = ev.target.value;
        this.setState(o);
    }

    goto = (page: string) => () => {
        this.props.history.push(page);
    }

    renderSportsProSyncStatus = () => {
        if (this.state.sportsProSyncStatusLoading) {
            return <LinearProgress color="primary" />;
        }
        const s = this.state.sportsProSyncStatus;
        if (!s) {
            return <span style={{ color: 'red', fontWeight: 'bold' }}>Kunde inte hämta synkstatus</span>;
        }
        const ok = s.diffSeconds < 60;
        return <Fragment>
            <span style={{ color: ok ? 'green' : 'red', fontWeight: 'bold', fontSize: '1.3em' }}>{ok ? 'OK' : 'EJ OK'}</span><br />
            Senast mottagen person: <b>{moment(s.lastReceivedPerson).format('YYYY-MM-DD HH:mm:ss')}</b><br />
            Senast skickad person: <b>{moment(s.lastSentPerson).format('YYYY-MM-DD HH:mm:ss')}</b><br />
            Diff: <b>{s.diffSeconds} s</b> {ok ? "(< 1 min)" : "(≥ 1 min)"}
        </Fragment>;
    }


    renderIceOverview = () => {
        if (this.state.iceOverviewLoading) {
            return <LinearProgress color="primary" />;
        }
        const o = this.state.iceOverview;
        if (!o || !o.races || o.races.length === 0) {
            return <span style={{ color: '#888' }}>Ingen aktuell evenemangsvecka med lopp hittades.</span>;
        }
        const totals = o.races.reduce((acc, r) => { acc.total += r.total; acc.withIce += r.withIce; return acc; }, { total: 0, withIce: 0 });
        const pct = (w: number, t: number) => t > 0 ? Math.round((w / t) * 100) : 0;
        return <Fragment>
            <Typography variant="body2" style={{ marginBottom: 6 }}>
                <b>{o.eventName}</b> — andel ACTIVE-deltagare som angett ICE-information
            </Typography>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9em' }}>
                <thead>
                    <tr style={{ textAlign: 'left', color: '#666' }}>
                        <th style={{ padding: '2px 4px' }}>Lopp</th>
                        <th style={{ padding: '2px 4px', textAlign: 'right' }}>Med ICE</th>
                        <th style={{ padding: '2px 4px', textAlign: 'right' }}>Totalt</th>
                        <th style={{ padding: '2px 4px', textAlign: 'right' }}>Andel</th>
                    </tr>
                </thead>
                <tbody>
                    {o.races.map(r =>
                        <tr key={r.productId} style={{ borderTop: '1px solid #eee' }}>
                            <td style={{ padding: '2px 4px' }}>{r.race}</td>
                            <td style={{ padding: '2px 4px', textAlign: 'right' }}><b>{r.withIce}</b></td>
                            <td style={{ padding: '2px 4px', textAlign: 'right' }}>{r.total}</td>
                            <td style={{ padding: '2px 4px', textAlign: 'right' }}>{pct(r.withIce, r.total)}%</td>
                        </tr>
                    )}
                    <tr style={{ borderTop: '2px solid #ddd', fontWeight: 'bold' }}>
                        <td style={{ padding: '2px 4px' }}>Totalt</td>
                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{totals.withIce}</td>
                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{totals.total}</td>
                        <td style={{ padding: '2px 4px', textAlign: 'right' }}>{pct(totals.withIce, totals.total)}%</td>
                    </tr>
                </tbody>
            </table>
        </Fragment>;
    }

    render() {
        let { classes } = this.props as any;
        return <FirebaseContext.Consumer>
            {(value: { firebase: Firebase, isSignedIn: boolean, profile: Profile }) => {
                return <div>
                    {value.isSignedIn &&
                        <Fragment>
                            <Typography variant="h4">
                                Välkommen {value.profile.firstName + " " + value.profile.lastName}
                            </Typography>
                            <Typography variant="body2">Hittar inte du det du söker efter, kontakta Sporten för att få tillgång</Typography></Fragment>
                    }

                    <Grid container className={classes.root} spacing={2}>
                        <Grid item xs={12} sm={6}>
                            {value.profile.isInRole('manageentries') &&
                                <Card>
                                    <CardHeader className={classes.cardHeader} title="Anmälningar" />
                                    <CardContent>
                                        {this.state.entryStatisticsLoading ? <LinearProgress color="primary" /> :
                                            <Fragment>
                                                Anmälningar idag: <b>{this.state.entryStatistics.entriesToday}</b><br />
                                                {(this.state.entryStatistics.upcomingEventEntries || []).map(ev =>
                                                    <div key={ev.eventId} style={{ marginLeft: 12 }}>{ev.name}: <b>{ev.count}</b></div>
                                                )}
                                                <br />
                                                Totalt antal målgångar: <b>{this.state.entryStatistics.numFinishes}</b></Fragment>}
                                    </CardContent>
                                    <CardActionArea component="a">
                                        <Button onClick={this.goto('/entries/manage')}>Hantera anmälningar</Button>
                                    </CardActionArea>
                                </Card>
                            }
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            {value.profile.isInRole('manageorders') &&
                                <Card>
                                    <CardHeader className={classes.cardHeader} title="Orders" />
                                    <CardContent>
                                        {this.state.orderStatisticsLoading ? <CircularProgress color="secondary" /> : <BarChart id="orderChart" data={this.state.orderStatistics} />}
                                    </CardContent>
                                    <CardActionArea component="a">
                                        <Button onClick={this.goto('/orders/manage')}>Hantera orders</Button>
                                    </CardActionArea>
                                </Card>
                            }
                        </Grid>
                        {value.profile.isInRole('admin') &&
                            <Grid item xs={12} sm={6}>
                                <Card>
                                    <CardHeader className={classes.cardHeader} title="Konton" />
                                    <CardContent>
                                        Hantera konton och användare
                                    </CardContent>
                                    <CardActionArea component="a">
                                        <Button onClick={this.goto('/users/manage')}>Hantera konton</Button></CardActionArea>
                                </Card>
                            </Grid>}

                        <Grid item xs={12} sm={6}>
                            <Card>
                                <CardHeader className={classes.cardHeader} title="SportsPro-synk" />
                                <CardContent>
                                    {this.renderSportsProSyncStatus()}
                                </CardContent>
                            </Card>
                        </Grid>

                        {value.profile.isInRole('manageentries') &&
                            <Grid item xs={12} sm={6}>
                                <Card>
                                    <CardHeader className={classes.cardHeader} title="ICE-information per lopp" />
                                    <CardContent>
                                        {this.renderIceOverview()}
                                    </CardContent>
                                </Card>
                            </Grid>
                        }

                    </Grid>
                </div>

            }}
        </FirebaseContext.Consumer>
    }
}

const useStyles = ({ palette, spacing }: Theme) => createStyles({
    cardHeader: {
        background: palette.secondary.main,
        color: palette.secondary.contrastText,
        padding: 3
    },
    photo: {
        height: '30px',
        verticalAlign: 'middle',
        borderRadius: '10px'
    },
    root: {

    },
    form: {
        '& > *': {
            margin: spacing(1),
            width: '25ch',
        },
        '& label.Mui-focused': {
            color: palette.secondary.main,
        },
        '& .MuiInput-underline:after': {
            borderBottomColor: palette.secondary.main,
        },
    }
}
);

export default withRouter(withStyles(useStyles)(LoggedIn));